import { salesBoxApi } from '../config/apis';
import { providesList } from '../helpers/provides';
import { URLs } from '../helpers/urls';
import { TagTypes } from './types';

export interface PriceRange {
  id: string;
  type: 'PF' | 'PJ';
  minQuantity: number;
  maxQuantity: number;
  value: number;
}

const priceRangeApi = salesBoxApi.injectEndpoints({
  endpoints: builder => ({
    getPriceRanges: builder.query<PriceRange[], 'PF' | 'PJ'>({
      query: type => ({
        url: URLs.combine('gateway/main/PriceRange'),
        params: { type },
      }),
      providesTags: response => providesList(response, TagTypes.PriceRange),
    }),
    createPriceRange: builder.mutation<PriceRange, Omit<PriceRange, 'id'>>({
      query: data => ({
        url: 'gateway/main/PriceRange',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: TagTypes.PriceRange, id: 'LIST' }],
    }),
    updatePriceRange: builder.mutation<
      PriceRange,
      { id: string; data: Omit<PriceRange, 'id'> }
    >({
      query: ({ id, data }) => ({
        url: URLs.combine('gateway/main/PriceRange', id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: TagTypes.PriceRange, id: 'LIST' }],
    }),
    deletePriceRange: builder.mutation<void, string>({
      query: id => ({
        url: URLs.combine('gateway/main/PriceRange', id),
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TagTypes.PriceRange, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetPriceRangesQuery,
  useCreatePriceRangeMutation,
  useUpdatePriceRangeMutation,
  useDeletePriceRangeMutation,
} = priceRangeApi;

// Funções de conveniência para manter compatibilidade com o código existente
export async function getPriceRanges(type: 'PF' | 'PJ'): Promise<PriceRange[]> {
  const authData = JSON.parse(localStorage?.getItem('auth') as string);

  const response = await fetch(
    `/portal/api/v1/gateway/main/PriceRange?type=${type}`,
    {
      headers: {
        Accept: '*/*',
        'Accept-Language':
          'pt-BR,pt;q=0.9,en-US;q=0.8,en;q=0.7,es-PE;q=0.6,es;q=0.5',
        Connection: 'keep-alive',
        'Content-Type': 'application/json',
        customerid: authData?.customer || '',
        'x-requested-with': 'XmlHttpRequest',
        'x-xsrf-token':
          document.cookie.match(/SALESBOX-PORTAL-XSRF-TOKEN=([^;]+)/)?.[1] ||
          '',
      },
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export async function createPriceRange(
  data: Omit<PriceRange, 'id'>,
): Promise<any> {
  const authData = JSON.parse(localStorage?.getItem('auth') as string);

  const response = await fetch('/portal/api/v1/gateway/main/PriceRange', {
    method: 'POST',
    headers: {
      Accept: '*/*',
      'Accept-Language':
        'pt-BR,pt;q=0.9,en-US;q=0.8,en;q=0.7,es-PE;q=0.6,es;q=0.5',
      Connection: 'keep-alive',
      'Content-Type': 'application/json',
      customerid: authData?.customer || '',
      'x-requested-with': 'XmlHttpRequest',
      'x-xsrf-token':
        document.cookie.match(/SALESBOX-PORTAL-XSRF-TOKEN=([^;]+)/)?.[1] || '',
    },
    body: JSON.stringify(data),
  });
  return response;
}

export async function updatePriceRange(
  id: string,
  data: Omit<PriceRange, 'id'>,
): Promise<any> {
  const authData = JSON.parse(localStorage?.getItem('auth') as string);

  const response = await fetch(`/portal/api/v1/gateway/main/PriceRange/${id}`, {
    method: 'PUT',
    headers: {
      Accept: '*/*',
      'Accept-Language':
        'pt-BR,pt;q=0.9,en-US;q=0.8,en;q=0.7,es-PE;q=0.6,es;q=0.5',
      Connection: 'keep-alive',
      'Content-Type': 'application/json',
      customerid: authData?.customer || '',
      'x-requested-with': 'XmlHttpRequest',
      'x-xsrf-token':
        document.cookie.match(/SALESBOX-PORTAL-XSRF-TOKEN=([^;]+)/)?.[1] || '',
    },
    body: JSON.stringify(data),
  });
  return response;
}

export async function deletePriceRange(id: string): Promise<any> {
  const authData = JSON.parse(localStorage?.getItem('auth') as string);

  const response = await fetch(`/portal/api/v1/gateway/main/PriceRange/${id}`, {
    method: 'DELETE',
    headers: {
      Accept: '*/*',
      'Accept-Language':
        'pt-BR,pt;q=0.9,en-US;q=0.8,en;q=0.7,es-PE;q=0.6,es;q=0.5',
      Connection: 'keep-alive',
      customerid: authData?.customer || '',
      'x-requested-with': 'XmlHttpRequest',
      'x-xsrf-token':
        document.cookie.match(/SALESBOX-PORTAL-XSRF-TOKEN=([^;]+)/)?.[1] || '',
    },
  });
  return response;
}
